import React, { useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import WidgetPaper from '@components/widgets/display-widget/WidgetPaper';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import { IBeneficiaryData, IProposalPageFormState } from './form';
import { validatePolicyBeneficiaryField } from '../validation';
import ProposalPolicyPersonDrawer from '../../proposal-policy-person-drawer/ProposalPolicyPersonDrawer';
import PersonAddressDrawer from '@modules/salesforce/persons/person-details-page/person-address-drawer/PersonAddressDrawer';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ClearIcon from '@mui/icons-material/Clear';
import { removeObjectAtIndex } from '@utils/helper-utils';
import { useLazyQuery } from '@apollo/client';
import {
  fetchedPersonAddressList,
  fetchedPersonsWithPagination,
} from '../queries';
import {
  extractPersonsAddressesFixed,
  extractPersonsFromQuery,
} from '../utils';
import { useParams } from 'react-router-dom';
import { cloneDeep, isEmpty } from 'lodash';
import { IProposalDetailsSummary } from './index2';
import PercentageFormField from '@components/form-fields/PercentageFormField';
import AutocompleteFormField from '@components/form-fields/AutocompleteFormField';
import WithLinkFormField from '@components/form-fields/WithLinkFormField';
import SelectFormField from '@components/form-fields/SelectFormField';
import { IAbstractRecord } from '@models';
import { useResetKey } from '../../../../../hooks/useResetKey';
import { MAIN_THEME } from '@styles';

interface IPolicyDetailsWidgetProps {
  pageState: IProposalPageFormState;
  onPageStateUpdate: (pageState: IProposalPageFormState) => void;
  disabledForm?: boolean;
  lovs: Record<string, Record<string, string>>;
  maxNumber: number;
  data: IProposalDetailsSummary;
}

const useStyles = makeStyles()(() => ({
  container: {
    width: '100%',
    backgroundColor: MAIN_THEME.color.white98,
    border: `1px solid ${MAIN_THEME.color.white92}`,
    borderRadius: '4px',
    padding: '33px 23px 23px',
    marginBottom: '14px',
    position: 'relative',
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: '39% 39% 18%',
    gap: '2%',
    justifyContent: 'flex-start',
    alignItems: 'baseline',
    alignContent: 'center',
  },
  addBtn: {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    margin: '20px auto 0',
    display: 'block',
  },
  removeBtn: {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    position: 'absolute',
    top: '8px',
    right: '0',
    margin: '0',
  },
  clearBtn: {
    display: 'flex',
    justifyContent: 'flex-end',
    margin: '0 !important',
  },
  label: {
    color: MAIN_THEME.color.black,
    margin: '0px!important',
    padding: '0px!important',
    fontSize: `${MAIN_THEME.fontSize.sm} !important`,
    lineHeight: '16px !important',
    fontFamily: `${MAIN_THEME.font.medium} !important`,
  },
}));

const PolicyBeneficiaryWidget: React.FC<IPolicyDetailsWidgetProps> = ({
  pageState,
  onPageStateUpdate,
  disabledForm,
  maxNumber,
  data,
}) => {
  const { classes } = useStyles();
  const params = useParams<{ id: string }>();
  const entityId = params.id;
  const [autocompleteKey, resetAutocomplete] = useResetKey('beneficiary');

  const [loaded, setLoaded] = useState<boolean>(false);

  const [newPersonDrawerOpen, setNewPersonDrawerOpen] = useState<number>(-1);
  const [newAddressDrawerOpen, setNewAddressDrawerOpen] = useState<number>(-1);
  const [currentSection, setCurrentSection] = useState<string | null>(null);

  const [lists, setLists] = useState<{
    id: Record<number, Record<string, string>>;
    beneficiaryAddresses: Record<number, Record<string, string>>;
    primaryAddresses: Record<number, string>;
  }>({
    id: {},
    beneficiaryAddresses: {},
    primaryAddresses: {},
  });

  const beneficiaryValues = pageState.values.policyPersons.beneficiaries;
  const beneficiaryErrors = pageState.errors.policyPersons.beneficiaries;
  const beneficiaryTouched = pageState.touched.policyPersons.beneficiaries;

  const [fetchedPersonsLazy] = useLazyQuery(fetchedPersonsWithPagination());
  const [getPersonAddressListLazy] = useLazyQuery(fetchedPersonAddressList());

  const handleOpenDrawer = (section: string, rowIndex: number) => {
    setCurrentSection(section);
    setNewPersonDrawerOpen(rowIndex);
  };

  const handleOpenAddressDrawer = (section: string, rowIndex: number) => {
    setCurrentSection(section);
    setNewAddressDrawerOpen(rowIndex);
  };

  const handleBeneficiaryAddresses = async (personId: string) => {
    const result = await getPersonAddressListLazy({
      variables: { selectedPersonID: personId },
      errorPolicy: 'all',
    });
    return extractPersonsAddressesFixed(result.data);
  };

  const initialize = () => {
    const newList: Record<number, Record<string, string>> = {};
    beneficiaryValues.forEach((beneficiary, index) => {
      if (!newList[index]) {
        newList[index] = {};
      }
      newList[index][beneficiary.address] = beneficiary.addressDetails;
    });
    setLists((prevLists) => ({
      ...prevLists,
      beneficiaryAddresses: newList,
    }));
    setLoaded(true);
  };

  useEffect(() => {
    initialize();
  }, []);

  const onFieldBlur = (fieldName: string, rowIndex: number) => {
    const newPageState = cloneDeep(pageState);
    newPageState.touched.policyPersons.beneficiaries[rowIndex][fieldName] =
      true;
    onPageStateUpdate(newPageState);
  };

  const onFieldUpdate = async (
    fieldName: string,
    value: unknown,
    rowIndex: number,
    touched = false,
    updatedList: typeof lists = lists
  ) => {
    const newPageState = cloneDeep(pageState);
    const newLists = { ...updatedList };

    if (fieldName === 'id') {
      (newPageState.values.policyPersons.beneficiaries[rowIndex] as any)[
        fieldName
      ] = value;
      newPageState.errors.policyPersons.beneficiaries[rowIndex][fieldName] =
        validatePolicyBeneficiaryField(fieldName, value);
      if (touched) {
        newPageState.touched.policyPersons.beneficiaries[rowIndex][fieldName] =
          true;
      }

      if (!isEmpty(value)) {
        const searchAddressResult = await handleBeneficiaryAddresses(
          value as string
        );
        const beneficiaryAddresses = searchAddressResult;
        const extractedAddresses: IAbstractRecord = {};

        if (
          beneficiaryAddresses &&
          Object.keys(beneficiaryAddresses).length > 0
        ) {
          Object.keys(beneficiaryAddresses).forEach((key) => {
            extractedAddresses[key] = beneficiaryAddresses[key].address;
            if (beneficiaryAddresses[key].isPrimary) {
              newPageState.values.policyPersons.beneficiaries[
                rowIndex
              ].address = key;
              newPageState.values.policyPersons.beneficiaries[
                rowIndex
              ].addressDetails = beneficiaryAddresses[key].address;
            }
          });
          newPageState.errors.policyPersons.beneficiaries[
            rowIndex
          ].beneficiaryAddress = '';
          newLists['beneficiaryAddresses'][rowIndex] = extractedAddresses;
        } else {
          newPageState.values.policyPersons.beneficiaries[rowIndex].address =
            '';
          newPageState.values.policyPersons.beneficiaries[
            rowIndex
          ].addressDetails = '';
          newPageState.errors.policyPersons.beneficiaries[
            rowIndex
          ].beneficiaryAddress = '';
          newLists['beneficiaryAddresses'][rowIndex] = {};
        }
      }
    } else {
      (newPageState.values.policyPersons.beneficiaries[rowIndex] as any)[
        fieldName
      ] = value;

      newPageState.errors.policyPersons.beneficiaries[rowIndex][fieldName] =
        validatePolicyBeneficiaryField(fieldName, value);
      if (touched) {
        newPageState.touched.policyPersons.beneficiaries[rowIndex][fieldName] =
          true;
      }
    }

    setLists(newLists);
    onPageStateUpdate(newPageState);
  };

  const onPersonAddition = async (person: string, personFullName?: string) => {
    const rowIndex = newPersonDrawerOpen;

    const newPageState = cloneDeep(pageState);

    const newLists = cloneDeep(lists);

    newPageState.values.policyPersons.beneficiaries[rowIndex]['id'] = person;
    newPageState.errors.policyPersons.beneficiaries[rowIndex]['id'] = '';
    newPageState.touched.policyPersons.beneficiaries[rowIndex]['id'] = true;

    newLists.id[rowIndex] = {};
    newLists.id[rowIndex][person] = personFullName;

    const searchAddressResult = await handleBeneficiaryAddresses(person);

    const beneficiaryAddresses = searchAddressResult;
    const extractedAddresses: IAbstractRecord = {};
    if (beneficiaryAddresses) {
      Object.keys(beneficiaryAddresses).forEach((key) => {
        extractedAddresses[key] = beneficiaryAddresses[key].address;
        if (beneficiaryAddresses[key].isPrimary) {
          newPageState.values.policyPersons.beneficiaries[rowIndex].address =
            key;
          newPageState.values.policyPersons.beneficiaries[
            rowIndex
          ].addressDetails = beneficiaryAddresses[key].address;
        }
      });
      newPageState.errors.policyPersons.beneficiaries[
        rowIndex
      ].beneficiaryAddress = '';
      newLists['beneficiaryAddresses'][rowIndex] = extractedAddresses;
    }

    if (Object.keys(newLists['beneficiaryAddresses'][rowIndex]).length === 1) {
      newPageState.values.policyPersons.beneficiaries[rowIndex]['address'] =
        Object.keys(newLists['beneficiaryAddresses'][rowIndex])[0];
    }

    resetAutocomplete();
    setLists(newLists);
    onPageStateUpdate(newPageState);
  };

  const onAddressAddition = async (
    address: string,
    addressFullName?: string
  ) => {
    const rowIndex = newAddressDrawerOpen;

    const newPageState = cloneDeep(pageState);

    const newLists = cloneDeep(lists);

    newPageState.values.policyPersons.beneficiaries[rowIndex]['address'] =
      address;
    newPageState.errors.policyPersons.beneficiaries[rowIndex]['address'] = '';
    newPageState.touched.policyPersons.beneficiaries[rowIndex]['address'] =
      true;

    newLists.beneficiaryAddresses[rowIndex] = {};
    newLists.beneficiaryAddresses[rowIndex][address] = addressFullName;

    newPageState.values.policyPersons.beneficiaries[rowIndex].address = address;
    newPageState.values.policyPersons.beneficiaries[rowIndex].addressDetails =
      addressFullName;

    setLists(newLists);
    onPageStateUpdate(newPageState);
  };

  const renderBeneficiaryRow = (row: IBeneficiaryData) => {
    const rowIndex = beneficiaryValues.indexOf(row);
    return (
      <div
        className={
          maxNumber === 1 && beneficiaryValues.length === 1
            ? undefined
            : classes.container
        }
        key={rowIndex}
      >
        <div className={classes.clearBtn}>
          {!disabledForm && beneficiaryValues.length > 1 && (
            <button
              className={classes.removeBtn}
              onClick={() => onBeneficiaryRowDelete(rowIndex)}
            >
              <ClearIcon fontSize="small" />
            </button>
          )}
        </div>
        <div className={classes.fieldRow}>
          <WithLinkFormField
            linkText={disabledForm ? '' : 'Add Person'}
            addSpace={beneficiaryValues.length === 1}
            onLinkClick={() => {
              handleOpenDrawer('beneficiary', rowIndex);
            }}
          >
            <AutocompleteFormField
              key={`${autocompleteKey}${rowIndex}`}
              name="id"
              title="Beneficiary"
              placeholder="Beneficiary"
              value={beneficiaryValues[rowIndex].id}
              error={
                beneficiaryTouched[rowIndex]?.id
                  ? beneficiaryErrors[rowIndex].id
                  : ''
              }
              disabled={disabledForm}
              material
              onChange={(v) => {
                onFieldUpdate('id', v, rowIndex);
              }}
              fetchOptions={function (
                value,
                pagination,
                hasDefaultValue
              ): Promise<{
                options: Record<string, string>;
                totalCount: number;
              }> {
                const defaultValue = hasDefaultValue ? value : null;
                return fetchedPersonsLazy({
                  variables: {
                    searchKeyword: hasDefaultValue ? null : value,
                    pagination,
                    selectedPersonID: defaultValue,
                  },
                }).then((result) => {
                  const options = extractPersonsFromQuery(
                    result.data?.Production?.queries
                      ?.GetAssignedPersonsWithPagination?.items
                  );
                  return {
                    options,
                    totalCount:
                      result.data?.Production?.queries
                        ?.GetAssignedPersonsWithPagination?.paging?.totalCount,
                  };
                });
              }}
            />
          </WithLinkFormField>

          <WithLinkFormField
            linkText={disabledForm ? '' : 'Add Address'}
            addSpace={beneficiaryValues.length <= 1}
            onLinkClick={() => {
              handleOpenAddressDrawer('beneficiary', rowIndex);
            }}
          >
            <SelectFormField
              key="address"
              name="address"
              title="Beneficiary Address"
              placeholder="Beneficiary Address"
              value={beneficiaryValues[rowIndex].address}
              disabled={disabledForm}
              material
              error={
                beneficiaryTouched[rowIndex]?.address
                  ? beneficiaryErrors[rowIndex].address
                  : ''
              }
              onChange={(v) => onFieldUpdate('address', v, rowIndex)}
              selectOptions={lists.beneficiaryAddresses[rowIndex]}
            />
          </WithLinkFormField>

          <PercentageFormField
            key="percentage"
            name="percentage"
            title="Percentage"
            placeholder="Percentage"
            value={beneficiaryValues[rowIndex].percentage}
            error={
              beneficiaryTouched[rowIndex].percentage
                ? beneficiaryErrors[rowIndex].percentage
                : ''
            }
            onBlur={() => onFieldBlur('percentage', rowIndex)}
            onChange={(v) =>
              onFieldUpdate('percentage', v.target.value, rowIndex)
            }
            disabled={disabledForm}
            material
          />
        </div>
      </div>
    );
  };

  const onBeneficiaryRowAdd = () => {
    if (!disabledForm && beneficiaryValues.length < maxNumber) {
      const newPageState = cloneDeep(pageState);
      newPageState.values.policyPersons.beneficiaries.push({
        id: '',
        address: '',
        percentage: 0,
      });

      newPageState.errors.policyPersons.beneficiaries.push({
        id: '',
        percentage: '',
        address: '',
      });

      newPageState.touched.policyPersons.beneficiaries.push({
        id: false,
        percentage: false,
        address: false,
      });

      onPageStateUpdate(newPageState);
    }
  };

  const onBeneficiaryRowDelete = (rowIndex: number) => {
    if (!disabledForm) {
      const newPageState = cloneDeep(pageState);
      newPageState.values.policyPersons.beneficiaries = removeObjectAtIndex(
        newPageState.values.policyPersons.beneficiaries,
        rowIndex
      );
      newPageState.errors.policyPersons.beneficiaries = removeObjectAtIndex(
        newPageState.errors.policyPersons.beneficiaries,
        rowIndex
      );
      newPageState.touched.policyPersons.beneficiaries = removeObjectAtIndex(
        newPageState.touched.policyPersons.beneficiaries,
        rowIndex
      );

      setLists((prevLists) => {
        const newId: Record<number, Record<string, string>> = {};
        const newBeneficiaryAddresses: Record<
          number,
          Record<string, string>
        > = {};

        Object.keys(prevLists.id).forEach((key) => {
          const keyNum = Number(key);
          if (keyNum < rowIndex) {
            newId[keyNum] = prevLists.id[keyNum];
            newBeneficiaryAddresses[keyNum] =
              prevLists.beneficiaryAddresses[keyNum];
          } else if (keyNum > rowIndex) {
            newId[keyNum - 1] = prevLists.id[keyNum];
            newBeneficiaryAddresses[keyNum - 1] =
              prevLists.beneficiaryAddresses[keyNum];
          }
        });

        return {
          id: newId,
          beneficiaryAddresses: newBeneficiaryAddresses,
          primaryAddresses: {},
        };
      });

      onPageStateUpdate(newPageState);
    }
  };

  if (!loaded) {
    return null;
  }

  return (
    <WidgetPaper>
      <WidgetSection
        title="Beneficiary"
        hasTitleSpecificDesign={false}
        useSeparator={false}
      >
        {beneficiaryValues.map((v) => renderBeneficiaryRow(v))}
        {newPersonDrawerOpen !== -1 && currentSection === 'beneficiary' && (
          <ProposalPolicyPersonDrawer
            open={newPersonDrawerOpen !== -1}
            onClose={() => setNewPersonDrawerOpen(-1)}
            section={currentSection}
            planId={''}
            lineExternalCode={data?.LineId?.ExternalCode}
            onSuccess={() => undefined}
            proposalId={entityId}
            onSubmitSuccess={onPersonAddition}
          />
        )}
        {newAddressDrawerOpen !== -1 && currentSection === 'beneficiary' && (
          <PersonAddressDrawer
            open={newAddressDrawerOpen !== -1}
            onClose={() => setNewAddressDrawerOpen(-1)}
            personId={beneficiaryValues?.[newAddressDrawerOpen]?.id || ''}
            onSuccess={() => undefined}
            onSubmitSuccess={onAddressAddition}
          />
        )}
        {beneficiaryValues.length < maxNumber && (
          <button
            className={classes.addBtn}
            onClick={() => onBeneficiaryRowAdd()}
          >
            <AddCircleOutlineIcon color="error" fontSize="small" />
          </button>
        )}
      </WidgetSection>
    </WidgetPaper>
  );
};

export default PolicyBeneficiaryWidget;
